import { useState } from 'react';

/**
 * What a settings control shows between "you clicked it" and "the server said so".
 *
 * ── The problem, and why the two obvious answers are both wrong ──
 *
 * These cards save one switch at a time and re-read the whole row. Between the click and the
 * refetch there is a window where the server's answer is still the OLD value, and rendering
 * it means the switch you just flipped visibly flips back and then forward again.
 *
 * The first spelling was a MIRRORED DRAFT — `useEffect` copying the server into local state.
 * That is the classic React bug: the copy goes stale, and a change made in another tab (or by
 * the colleague sitting opposite) never reaches the screen.
 *
 * The second was an override filtered BY VALUE: keep the local value while it differs from
 * the server, drop it when they agree. That is what shipped, and it has two defects that took
 * an adversary to find:
 *
 *  🔴 **The dormant override resurrects.** Turning reminders on left `{remindersEnabled: true}`
 *     in the override map forever — filtered out only while the server also said `true`. When
 *     someone later turned it OFF, the server said `false`, the values differed again, and the
 *     override came back from the dead to hide their change. Deterministic: enable in Getting
 *     paid, let it settle, disable in Automations, watch Getting paid say on.
 *
 *  🔴 **A value the server NORMALIZES never settles.** `updateAutomations` sorts the follow-up
 *     offsets, so an override of `[7, 5]` was compared against a stored `[5, 7]` and never
 *     matched. The card then showed first=7/second=5 for the life of the mount while the sweep
 *     actually ran 5 then 7 — a settings screen disagreeing with the automation it configures.
 *
 * ── The rule this uses instead ──
 *
 * ⭐ **An override is not a value, it is a bet against a WATERMARK.** Stamp each key with the
 * `dataUpdatedAt` it was written against, and drop it the moment the query has anything newer
 * — whatever that newer thing says. The server is then authoritative the instant it speaks,
 * so nothing can resurrect and nothing has to be compared. Both defects above are unreachable
 * rather than fixed, and `sameValue` (which existed only to do the comparing) is gone.
 *
 * ⚠️ **A FAILED refetch does not move the watermark**, which is the behaviour we want: the
 * write landed, the read did not, so the person keeps seeing what they saved rather than a
 * stale value that contradicts it. `StaleNotice` is what says so out loud.
 *
 * ⚠️ `holdWhile` covers the one race the watermark alone loses: a focus-refetch landing while
 * the write is still in flight carries the OLD value and a NEWER stamp, which would drop the
 * override and flash the control back for as long as the save takes. Superseded keys are
 * pruned on every `record`, so the set being held is only ever writes the server has not yet
 * answered.
 */
export type Stamped = { value: unknown; at: number };
export type PendingMap = Record<string, Stamped>;

/**
 * Which overrides the server has NOT answered yet.
 *
 * ⚠️ Pure and exported so it can be table-tested, for the reason `worstState` is: both bugs
 * above are invisible from outside a rendered card, and this repo has shipped guards that
 * could not fail because the property they described was only observable through the
 * component they were guarding.
 */
export function liveOverrides(
  pending: PendingMap,
  dataUpdatedAt: number,
  holdWhile = false,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(pending)
      .filter(([, p]) => holdWhile || dataUpdatedAt <= p.at)
      .map(([k, p]) => [k, p.value]),
  );
}

/** The map after a new patch: superseded keys dropped, the patch stamped and added. */
export function recordInto(
  prev: PendingMap,
  patch: Record<string, unknown>,
  dataUpdatedAt: number,
): PendingMap {
  return {
    // Prune first: anything the server has already answered is dead, and pruning here rather
    // than at render is what keeps `holdWhile` honest — the set it holds then contains only
    // writes still outstanding, never a stale one waiting to be resurrected.
    ...Object.fromEntries(Object.entries(prev).filter(([, p]) => dataUpdatedAt <= p.at)),
    ...Object.fromEntries(
      Object.entries(patch).map(([k, v]) => [k, { value: v, at: dataUpdatedAt }]),
    ),
  };
}

export interface PendingOverride<T extends object> {
  /** The server's row with any un-answered local writes laid over it. */
  apply: (server: T, dataUpdatedAt: number, holdWhile?: boolean) => T;
  /** Remember a patch, stamped against the data it was written to. Call before `mutate`. */
  record: (patch: Partial<T>, dataUpdatedAt: number) => void;
  /** Forget everything — for a REFUSED save, where the server's answer is the truth. */
  clear: () => void;
}

export function usePendingOverride<T extends object>(): PendingOverride<T> {
  const [pending, setPending] = useState<PendingMap>({});

  return {
    apply: (server, dataUpdatedAt, holdWhile = false) =>
      ({ ...server, ...liveOverrides(pending, dataUpdatedAt, holdWhile) }) as T,
    record: (patch, dataUpdatedAt) =>
      setPending((prev) =>
        recordInto(prev, patch as Record<string, unknown>, dataUpdatedAt),
      ),
    clear: () => setPending({}),
  };
}
